export interface AdminOperationsScope {
  scopeId?: number;
  owningCompanyId?: number;
  scopeCode?: string;
  scopeName?: string;
  scopeType?: string;
  linkedCompanyId?: number;
  locationId?: number;
  warehouseId?: number;
  description?: string;
  active?: boolean;
}

export interface AdminOperationsAuthority {
  authorityId?: number;
  owningCompanyId?: number;
  authorityCode?: string;
  authorityName?: string;
  portalName?: string;
  portalUrl?: string;
  countryCode?: string;
  category?: string;
  instructions?: string;
  nonSecretAccountReference?: string;
  active?: boolean;
}

export interface AdminOperationsObligation {
  obligationId?: number;
  owningCompanyId?: number;
  obligationCode?: string;
  obligationName?: string;
  purpose?: string;
  obligationType?: string;
  frequencyCode?: string;
  scopeId?: number;
  scopeName?: string;
  authorityId?: number;
  authorityName?: string;
  portalUrl?: string;
  ownerEmployeeId?: number;
  ownerName?: string;
  approverEmployeeId?: number;
  approverName?: string;
  firstDueDate?: string;
  effectiveFrom?: string;
  effectiveTo?: string;
  evidenceRequired?: boolean;
  approvalRequired?: boolean;
  documentExpiryDriven?: boolean;
  cycleWarningDaysCsv?: string;
  documentWarningDaysCsv?: string;
  confidentialityLevel?: string;
  active?: boolean;
}

export interface AdminOperationsEvidence {
  evidenceId?: number;
  cycleId?: number;
  evidenceType?: string;
  fileName?: string;
  fileUrl?: string;
  fileContentType?: string;
  description?: string;
  confidentialityLevel?: string;
  createdAt?: string;
}

export interface AdminOperationsCycle {
  cycleId?: number;
  obligationId?: number;
  obligationCode?: string;
  obligationName?: string;
  category?: string;
  scopeId?: number;
  scopeName?: string;
  authorityName?: string;
  ownerEmployeeId?: number;
  ownerName?: string;
  dueDate?: string;
  workflowStatus?: string;
  riskStatus?: string;
  daysUntilDue?: number;
  completionNotes?: string;
  evidenceRequired?: boolean;
  approvalRequired?: boolean;
  evidence?: AdminOperationsEvidence[];
}

export interface AdminOperationsDocument {
  documentId?: number;
  owningCompanyId?: number;
  obligationId?: number;
  cycleId?: number;
  previousDocumentId?: number;
  documentName?: string;
  obligationName?: string;
  documentNumber?: string;
  issuer?: string;
  issueDate?: string;
  expiryDate?: string;
  daysLeft?: number;
  fileName?: string;
  fileUrl?: string;
  fileContentType?: string;
  verificationStatus?: string;
  documentStatus?: string;
  confidentialityLevel?: string;
  rejectionReason?: string;
  notes?: string;
  active?: boolean;
}

export interface AdminOperationsBreakdown {
  key?: string;
  label?: string;
  total?: number;
  overdue?: number;
  dueSoon?: number;
}

export interface AdminOperationsDashboard {
  totalObligations?: number;
  pendingCycles?: number;
  dueThisMonth?: number;
  overdueCycles?: number;
  expiring30Days?: number;
  expiring60Days?: number;
  expiring90Days?: number;
  expiredDocuments?: number;
  pendingDocumentVerification?: number;
  riskByScope?: AdminOperationsBreakdown[];
  riskByCategory?: AdminOperationsBreakdown[];
  ownerWorkload?: AdminOperationsBreakdown[];
  attentionCycles?: AdminOperationsCycle[];
  attentionDocuments?: AdminOperationsDocument[];
}

export interface AdminOperationsCalendarRow {
  obligationId?: number;
  obligationName?: string;
  purpose?: string;
  frequencyCode?: string;
  scopeName?: string;
  authorityName?: string;
  months?: Record<number, AdminOperationsCycle>;
}

export interface AdminOperationsCalendar {
  year?: number;
  rows?: AdminOperationsCalendarRow[];
}

export interface AdminOperationsImportRow {
  rowNumber?: number;
  category?: string;
  scopeCode?: string;
  scopeName?: string;
  scopeType?: string;
  authorityCode?: string;
  authorityName?: string;
  portalName?: string;
  portalUrl?: string;
  obligationCode?: string;
  obligationName?: string;
  purpose?: string;
  obligationType?: string;
  frequencyCode?: string;
  firstDueDate?: string;
  ownerEmployeeNumber?: string;
  evidenceRequired?: boolean;
  approvalRequired?: boolean;
  documentName?: string;
  documentNumber?: string;
  issueDate?: string;
  expiryDate?: string;
  errors?: string[];
}

export interface AdminOperationsImportPreview {
  fileName?: string;
  totalRows?: number;
  validRows?: number;
  duplicateRows?: number;
  errorRows?: number;
  rows?: AdminOperationsImportRow[];
  errors?: string[];
}

export const ADMIN_OPERATION_TYPES = [
  'PORTAL_VISIT', 'FILING', 'REGISTRATION', 'LICENSE_RENEWAL',
  'INSPECTION', 'MEMBERSHIP', 'CONTRACT_RENEWAL', 'OTHER'
];

export const ADMIN_OPERATION_FREQUENCIES = [
  'ONCE', 'MONTHLY', 'QUARTERLY', 'SEMIANNUAL', 'ANNUAL', 'AS_NEEDED'
];

export const ADMIN_OPERATION_WORKFLOW_STATES = [
  'PENDING', 'IN_PROGRESS', 'SUBMITTED', 'COMPLETED', 'NOT_APPLICABLE', 'WAIVED'
];
